import { PermissionCode } from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminTableFrameClassName } from '@/platform/features/admin-panel';
import { MasterDataListState } from '@/platform/features/master-data/master-data-list-state';
import {
  useCreatePaymentMethod,
  useCreatePaymentTerm,
  usePaymentMethods,
  usePaymentTerms,
} from '@/platform/features/master-data/use-master-data';

const termFormSchema = z.object({
  code: z.string().trim().min(1).max(40),
  baseline: z.enum(['document_date', 'month_end']),
  netDays: z.number().int().nonnegative(),
  validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  discountDays: z.number().int().nonnegative().optional(),
  discountBps: z.number().int().min(1).max(10_000).optional(),
});

const BASELINE_LABEL = {
  document_date: 'Belegdatum',
  month_end: 'Monatsende',
} as const;

function numberOrUndefined(value: string): number | undefined {
  if (value.trim() === '') {
    return undefined;
  }
  return Number(value);
}

export function PaymentTermsPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.MASTER_DATA_MANAGE);
  const terms = usePaymentTerms();
  const methods = usePaymentMethods();

  return (
    <section className="space-y-8">
      <PlatformPageHeader
        title="Zahlungen"
        description="Zahlungsbedingungen, Arten und Skonto. Lastschrift ist hier nur ein Stammdatencode."
      />
      <MasterDataListState
        pending={terms.isPending}
        pendingLabel="Zahlungsbedingungen werden geladen …"
        error={terms.isError ? terms.error : null}
        errorLabel="Die Zahlungsbedingungen konnten nicht geladen werden."
        empty={terms.data?.items.length === 0}
        emptyLabel="Noch keine Zahlungsbedingung."
      >
        <div className={adminTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Grundlage</TableHead>
                <TableHead className="text-right">Netto-Tage</TableHead>
                <TableHead>Skonto</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {terms.data?.items.map((item) => {
                const open = item.versions.find((version) => version.validTo == null);
                const closed = item.versions.filter((version) => version.validTo != null);
                return (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-sm">{item.code}</TableCell>
                    <TableCell>{open ? BASELINE_LABEL[open.baseline] : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {open?.netDays ?? '—'}
                    </TableCell>
                    <TableCell>
                      {open && open.discounts.length > 0
                        ? open.discounts
                            .map((row) => `${row.days} Tage, ${row.discountBps} Basispunkte`)
                            .join('; ')
                        : '—'}
                      {closed.length > 0 ? (
                        <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                          {closed.map((version) => (
                            <li key={version.id}>
                              historisch · {version.netDays} Tage · bis {version.validTo}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {item.isActive ? (
                        <Badge variant="outline">Aktiv</Badge>
                      ) : (
                        <Badge variant="secondary">inaktiv</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </MasterDataListState>
      {canWrite ? <PaymentTermForm /> : null}
      <MasterDataListState
        pending={methods.isPending}
        pendingLabel="Zahlungsarten werden geladen …"
        error={methods.isError ? methods.error : null}
        errorLabel="Die Zahlungsarten konnten nicht geladen werden."
        empty={methods.data?.items.length === 0}
        emptyLabel="Noch keine Zahlungsart."
      >
        <ul className="space-y-1 text-sm">
          {methods.data?.items.map((item) => (
            <li key={item.id}>
              {item.name} <span className="font-mono text-muted-foreground">{item.code}</span>
              {item.isActive ? '' : ' · inaktiv'}
            </li>
          ))}
        </ul>
      </MasterDataListState>
      {canWrite ? <PaymentMethodForm /> : null}
    </section>
  );
}

function PaymentTermForm() {
  const create = useCreatePaymentTerm();
  const form = useForm({
    resolver: zodResolver(termFormSchema),
    defaultValues: {
      code: '',
      baseline: 'document_date' as const,
      netDays: 0,
      validFrom: '',
      discountDays: undefined as number | undefined,
      discountBps: undefined as number | undefined,
    },
  });

  async function onValid(values: z.infer<typeof termFormSchema>) {
    const discounts =
      values.discountDays != null && values.discountBps != null
        ? [{ days: values.discountDays, discountBps: values.discountBps }]
        : [];
    try {
      await create.mutateAsync({
        code: values.code,
        baseline: values.baseline,
        netDays: values.netDays,
        validFrom: values.validFrom,
        discounts,
      });
      toast.success('Zahlungsbedingung angelegt.');
      form.reset();
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Zahlungsbedingung konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <form className="max-w-xl space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="term-code">Code</Label>
        <Input id="term-code" {...form.register('code')} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="term-baseline">Grundlage</Label>
        <Select
          value={form.watch('baseline')}
          onValueChange={(value) =>
            form.setValue('baseline', value as 'document_date' | 'month_end')
          }
        >
          <SelectTrigger id="term-baseline" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="document_date">Belegdatum</SelectItem>
            <SelectItem value="month_end">Monatsende</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="term-net">Netto-Tage</Label>
        <Input
          id="term-net"
          type="number"
          min={0}
          {...form.register('netDays', { valueAsNumber: true })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="term-days">Skontotage</Label>
        <Input
          id="term-days"
          type="number"
          min={0}
          {...form.register('discountDays', { setValueAs: numberOrUndefined })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="term-bps">Skonto-Basispunkte</Label>
        <Input
          id="term-bps"
          type="number"
          min={1}
          {...form.register('discountBps', { setValueAs: numberOrUndefined })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="term-from">Gültig ab</Label>
        <Input id="term-from" placeholder="JJJJ-MM-TT" {...form.register('validFrom')} />
      </div>
      <Button type="submit" disabled={create.isPending}>
        Zahlungsbedingung anlegen
      </Button>
    </form>
  );
}

function PaymentMethodForm() {
  const create = useCreatePaymentMethod();
  const form = useForm({
    defaultValues: { code: 'bank_transfer' as 'bank_transfer' | 'direct_debit', name: '' },
  });

  async function onValid(values: { code: 'bank_transfer' | 'direct_debit'; name: string }) {
    try {
      await create.mutateAsync(values);
      toast.success('Zahlungsart angelegt.');
      form.reset();
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Zahlungsart konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <form className="max-w-xl space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="method-code">Code</Label>
        <Select
          value={form.watch('code')}
          onValueChange={(value) =>
            form.setValue('code', value as 'bank_transfer' | 'direct_debit')
          }
        >
          <SelectTrigger id="method-code" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bank_transfer">Überweisung</SelectItem>
            <SelectItem value="direct_debit">Lastschrift</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="method-name">Bezeichnung</Label>
        <Input id="method-name" {...form.register('name')} />
      </div>
      <Button type="submit" variant="outline" disabled={create.isPending}>
        Zahlungsart anlegen
      </Button>
    </form>
  );
}
